import {
  ArrowRight,
  HelpCircle,
  History,
  Home,
  Info,
  Keyboard,
  Maximize2,
  Minimize2,
  MessageSquare,
  MoreHorizontal,
  RotateCw,
  Sparkles,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@/shared/components/Accordion';
import AskAQuestionIcon from '@/assets/icons/AskAQuestionIcon';
import ChatView from './ChatView';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import Tooltip from '@/shared/components/Tooltip';
import './AlphaModal.css';
import { sendMessage, sendAudio, resetThreadId } from '../alpha.service';
import { useRuntimeConfigContext } from '@/shared/providers/RuntimeConfigProvider';
import {
  startAudioRecording,
  stopAudioRecording,
  abortAudioRecording,
} from '../services/audio.service';

interface AlphaModalProps {
  onClose: () => void;
}

interface Message {
  text: string;
  isUser: boolean;
  payload?: any;
  timestamp: string;
}

const quickQuestions = [
  'What is the average daily revenue miles?',
  'Can you tell me the average daily revenue hours?',
  'What is the average number of daily completed trips?',
  'What is the rides-per-hour performance?',
  'What is the current pick-up on-time performance (OTP)?',
  'What is the current drop-off on-time performance (OTP)?',
];

type View = 'home' | 'chat';

const AlphaModal: React.FC<AlphaModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { getConfigValue } = useRuntimeConfigContext();
  const convUiBaseUrl = getConfigValue<string>('web.ccagent.conv_base_url') || undefined;
  const [view, setView] = useState<View>('home');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [listening, setListening] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = async (question: string, timestamp: string) => {
    if (!question.trim()) return;

    setView('chat');
    const newUserMessage = { text: question, isUser: true, timestamp };
    setMessages((prev) => [...prev, newUserMessage]);
    setLoading(true);
    try {
      const response = await sendMessage(question, convUiBaseUrl);
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        { text: '', isUser: false, payload: response, timestamp: aiTimestamp },
      ]);
    } catch (error) {
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        {
          text: t('alpha.errorGeneric'),
          isUser: false,
          timestamp: aiTimestamp,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendAudio = async (audioBlob: Blob) => {
    setView('chat');
    setLoading(true);
    try {
      const response = await sendAudio(audioBlob, convUiBaseUrl);
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        { text: '', isUser: false, payload: response, timestamp: aiTimestamp },
      ]);
    } catch (error) {
      const aiTimestamp = new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      setMessages((prev) => [
        ...prev,
        {
          text: t('alpha.errorAudio'),
          isUser: false,
          timestamp: aiTimestamp,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleFooterNav = (selectedView: View) => {
    setView(selectedView);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Close modal on Escape key
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (!event.ctrlKey) return;

      // Ctrl+R: restart conversation
      if (event.key === 'r') {
        event.preventDefault();
        setMessages([]);
        setView('chat');
        setShowMenu(false);
        return;
      }

      // Ctrl+H: toggle history / more options menu
      if (event.key === 'h') {
        event.preventDefault();
        setShowMenu((prev) => !prev);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed bottom-2 right-[54px] z-50">
      <div
        className={`relative flex flex-col rounded-[10px] bg-surface shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden transition-all duration-300 h-[650px] max-h-[calc(100vh-4rem-0.5rem)] ${
          isMaximized ? 'w-[864px]' : 'w-[418px]'
        }`}
      >
        <div className="flex items-center justify-between bg-header p-3 text-on-primary">
          <div className="flex items-center">
            <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary">
              <Sparkles />
            </div>
            <div>
              <h2 className="font-medium text-base leading-none">
                {view === 'chat' ? t('alpha.messages') : t('alpha.title')}
              </h2>
              <div className="flex items-center">
                <div className="mr-1 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'rgb(var(--color-online))' }}></div>
                <p className="text-xs py-1">{t('alpha.online')}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div ref={menuRef} className="relative">
              <Tooltip text={t('alpha.moreOptions')} position="bottom" align="left">
                <button onClick={() => setShowMenu(!showMenu)} aria-label={t('alpha.moreOptions')}>
                  <MoreHorizontal size={24} aria-hidden="true" />
                </button>
              </Tooltip>
              {showMenu && (
                <div className="absolute top-full right-0 mt-2 w-60 rounded-lg bg-surface py-2 shadow-lg ring-1 ring-border z-50">
                  <ul>
                    <li>
                      <button
                        onClick={() => {
                          setShowShortcuts(true);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-left text-sm text-text hover:bg-surface-alt"
                      >
                        <Keyboard size={16} className="mr-3 text-primary" />
                        {t('alpha.keyboardShortcuts')}
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => {
                          resetThreadId();
                          setMessages([]);
                          setView('chat');
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center px-4 py-2 text-left text-sm text-text hover:bg-surface-alt"
                      >
                        <RotateCw size={16} className="mr-3 text-primary" />
                        {t('alpha.restartConversation')}
                      </button>
                    </li>
                    <li>
                      <button className="flex w-full items-center px-4 py-2 text-left text-sm text-text hover:bg-surface-alt">
                        <History size={16} className="mr-3 text-primary" />
                        {t('alpha.previousConversations')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <Tooltip
              text={isMaximized ? t('alpha.minimizeView') : t('alpha.maximizeView')}
              position="bottom"
              align="right"
            >
              <button
                onClick={() => setIsMaximized(!isMaximized)}
                aria-label={isMaximized ? t('alpha.minimizeView') : t('alpha.maximizeView')}
              >
                {isMaximized ? (
                  <Minimize2 size={24} aria-hidden="true" />
                ) : (
                  <Maximize2 size={24} aria-hidden="true" />
                )}
              </button>
            </Tooltip>
            <Tooltip
              text={isMuted ? t('alpha.unmuteAi') : t('alpha.muteAi')}
              position="bottom"
              align="right"
            >
              <button
                onClick={() => setIsMuted(!isMuted)}
                aria-label={isMuted ? t('alpha.unmuteAi') : t('alpha.muteAi')}
              >
                {isMuted ? (
                  <VolumeX size={24} aria-hidden="true" />
                ) : (
                  <Volume2 size={24} aria-hidden="true" />
                )}
              </button>
            </Tooltip>
            <Tooltip text={t('alpha.close')} position="bottom" align="right">
              <button onClick={onClose} aria-label={t('alpha.close')}>
                <X size={24} aria-hidden="true" />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="flex-grow overflow-y-auto">
          {view === 'home' && (
            <>
              <div className="welcome-gradient p-6 text-on-primary">
                <h1 className="text-2xl font-semibold leading-[30px] mb-2">
                  {t('alpha.welcomeTitle')}
                </h1>
                <p className="text-sm font-normal leading-4">
                  {t('alpha.welcomeSubtitle')}
                </p>
              </div>
              <div className="@container">
                <div className="flex flex-col">
                  <div className="p-4">
                    <button
                      onClick={() => {
                        setView('chat');
                        setMessages([]);
                      }}
                      className="group flex w-full items-center justify-between border border-border bg-surface p-6 text-left ask-a-question-button"
                    >
                      <div className="flex items-center">
                        <div className="mr-4 transition-transform duration-200 group-hover:scale-110">
                          <AskAQuestionIcon />
                        </div>
                        <div>
                          <p className="font-medium transition-colors duration-200 group-hover:text-primary">
                            {t('alpha.askAQuestion')}
                          </p>
                          <p className="text-sm text-text-muted">
                            {t('alpha.aiPoweredSupport')}
                          </p>
                        </div>
                      </div>
                      <ArrowRight strokeWidth={3} className="text-primary transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    <div className="mt-6">
                      <h3 className="mb-2 quick-questions-header">
                        {t('alpha.quickQuestions')}
                      </h3>
                      {quickQuestions.map((q) => (
                        <Accordion
                          key={q}
                          title={q}
                          titleClassName="quick-question-title transition-colors duration-200 group-hover:text-primary"
                          icon={
                            <HelpCircle
                              size={20}
                              className="text-primary"
                            />
                          }

                          onClick={() => {
                            const timestamp = new Date().toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            handleSendMessage(q, timestamp);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mt-6 h-full rounded-lg bg-surface-alt p-4 text-sm">
                      <div className="flex items-center">
                        <div className="mr-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-info/10">
                          <Info size={16} className="text-info" />
                        </div>
                        <h3 className="font-medium text-text">{t('alpha.proTip')}</h3>
                      </div>
                      <p className="mt-2 text-text-muted">
                        {t('alpha.proTipDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {view === 'chat' && (
            <ChatView
              messages={messages}
              loading={loading}
              onSendMessage={handleSendMessage}
              isMuted={isMuted}
              listening={listening}
              isMaximized={isMaximized}
              {...(convUiBaseUrl !== undefined && { convUiBaseUrl })}
              startListening={async () => {
                await startAudioRecording();
                setListening(true);
              }}
              stopListening={async () => {
                const audioBlob = await stopAudioRecording();
                if (audioBlob && audioBlob.size > 0) {
                  handleSendAudio(audioBlob);
                }
                setListening(false);
              }}
              abortListening={() => {
                abortAudioRecording();
                setListening(false);
              }}
              onRestart={() => {
                resetThreadId();
                setMessages([]);
                setView('home');
              }}
              onToggleMute={() => {
                setIsMuted(!isMuted);
              }}
              onShowShortcuts={() => {
                setShowShortcuts(true);
              }}
            />
          )}
        </div>

        <div className="flex w-full justify-around border-t border-border bg-surface p-2">
          <button
            onClick={() => handleFooterNav('home')}
            className={`flex flex-col items-center rounded-lg px-4 py-2 ${
              view === 'home' ? 'text-primary' : 'text-text-muted'
            }`}
          >
            <Home size={20} />
            <span className="text-xs">{t('alpha.home')}</span>
          </button>
          <button
            onClick={() => handleFooterNav('chat')}
            className={`flex flex-col items-center rounded-lg px-4 py-2 ${
              view === 'chat' ? 'text-primary' : 'text-text-muted'
            }`}
          >
            <MessageSquare size={20} />
            <span className="text-xs">{t('alpha.messages')}</span>
          </button>
        </div>
        {showShortcuts && <KeyboardShortcutsModal onClose={() => setShowShortcuts(false)} />}
      </div>
    </div>
  );
};

export default AlphaModal;